import React, { useState, useEffect } from "react";
import { collection, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useApp } from "../App";
import { ITEMS, SIZES, ITEM_CATEGORY_MAP } from "../data/items";
import { IconCamera } from "../components/Icons";

const CLOUDINARY_CLOUD = "dhjnbxsdk";
const CLOUDINARY_PRESET = "kepti_listings";

export default function PostListing({ onClose }) {
  const { t, user, lang } = useApp();
  const [itemName, setItemName] = useState("");
  const [size, setSize] = useState("");
  const [price, setPrice] = useState("");
  const [condition, setCondition] = useState("good");
  const [payment, setPayment] = useState("both");
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("");
  const [waNumber, setWaNumber] = useState("");
  const [noWA, setNoWA] = useState(false);

  const items = ITEMS[lang] || ITEMS.en;

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, "users", user.uid)).then(d => {
      if (d.exists()) setWaNumber(d.data().whatsapp || "");
    });
  }, [user]);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleItemSelect = (e) => {
    const val = e.target.value;
    setItemName(val);
    for (const [key, arr] of Object.entries(items)) {
      if (arr.includes(val)) { setCategory(ITEM_CATEGORY_MAP[key] || ""); break; }
    }
    const itemsEn = ITEMS.en;
    for (const [key, arr] of Object.entries(itemsEn)) {
      if (arr.includes(val)) { setCategory(ITEM_CATEGORY_MAP[key] || ""); break; }
    }
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_PRESET);
    formData.append("folder", "kepti_listings");
    const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`, { method: "POST", body: formData });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error?.message || "Upload failed");
    return data.secure_url;
  };

  const handleSubmit = async () => {
    if (!waNumber) { setNoWA(true); return; }
    if (!itemName || !size || !price) return;
    setLoading(true);
    try {
      let imageUrl = null;
      if (photo) imageUrl = await uploadToCloudinary(photo);
      await addDoc(collection(db, "listings"), {
        itemName, size, price: Number(price), condition, payment,
        note, imageUrl, category, status: "active",
        userId: user.uid, userName: user.displayName,
        userWhatsApp: waNumber, createdAt: serverTimestamp(),
      });
      onClose();
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const categoryKeys = Object.keys(items);
  const catLabelMap = {
    formalUniform: t.formalUniform, dailyUniform: t.dailyUniform,
    peUniform: t.peUniform, swimmingItems: t.swimmingItems,
    housesItems: t.housesItems, winterItems: t.winterItems,
    accessoriesItems: t.accessoriesItems,
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet">
        <div className="modal-handle-wrap"><div className="modal-handle" /></div>
        <div className="modal-header">
          <h2 className="modal-title">{t.sellItem}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {noWA && (
          <div style={{ background: "#fdf0f0", border: "1px solid #c0392b", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#c0392b", lineHeight: 1.5 }}>
            Please add your WhatsApp number in Profile first — buyers need it to contact you.
          </div>
        )}

        <div className="form-group">
          <label className="form-label">{t.item}</label>
          <select className="form-select" value={itemName} onChange={handleItemSelect}>
            <option value="">{t.selectItem}</option>
            {categoryKeys.map(key => (
              <optgroup key={key} label={catLabelMap[key] || key}>
                {(items[key] || []).map(item => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t.size}</label>
            <select className="form-select" value={size} onChange={e => setSize(e.target.value)}>
              <option value="">{t.selectSize}</option>
              {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t.price}</label>
            <input className="form-input" type="number" min="1" max="200" placeholder="e.g. 15" value={price} onChange={e => setPrice(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t.condition}</label>
          <div className="pill-group">
            {["likeNew", "good", "used"].map(c => (
              <div key={c} className={`pill ${condition === c ? "selected" : ""}`} onClick={() => setCondition(c)}>{t[c]}</div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t.payment}</label>
          <div className="pill-group">
            {["mbway", "cash", "both"].map(p => (
              <div key={p} className={`pill ${payment === p ? "selected" : ""}`} onClick={() => setPayment(p)}>{t[p]}</div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Photo</label>
          <div className="photo-upload">
            <input type="file" accept="image/*" onChange={handlePhoto} />
            {photoPreview
              ? <img src={photoPreview} alt="preview" />
              : <><IconCamera /><span style={{ marginTop: 4, fontSize: 13, color: "var(--text-muted)" }}>{t.addPhoto}</span></>
            }
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t.note}</label>
          <textarea className="form-textarea" placeholder={t.notePlaceholder} value={note} onChange={e => setNote(e.target.value)} />
        </div>

        <button className="btn-primary" onClick={handleSubmit} disabled={loading || !itemName || !size || !price}>
          {loading ? <div className="spinner" /> : t.postListing}
        </button>
      </div>
    </div>
  );
}
